import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Mail, Phone } from 'lucide-react';
import { contactInfo } from '../data/mock';

const TermsOfService = () => {
  const lastUpdated = "February 1, 2026";

  const sections = [
    {
      title: "1. Acceptance of Terms",
      content: (
        <>
          <p>
            Welcome to OmniCore X Quantum. These Terms of Service (&ldquo;Terms&rdquo;) constitute a legally binding agreement between you (&ldquo;user&rdquo;, &ldquo;client&rdquo;, &ldquo;you&rdquo;) and OmniCore X Quantum (&ldquo;we&rdquo;, &ldquo;our&rdquo;, &ldquo;us&rdquo;) governing your access to and use of our website (<span className="text-cyan-400">omnicorexquantum.com</span>), products, and services.
          </p>
          <p className="mt-4">
            By accessing our website, requesting a consultation, engaging our services, or otherwise interacting with us, you acknowledge that you have read, understood, and agreed to be bound by these Terms and our Privacy Policy. If you do not agree, you must not use our website or services.
          </p>
        </>
      )
    },
    {
      title: "2. About Our Services",
      content: (
        <>
          <p>
            OmniCore X Quantum is a digital marketing agency providing services including but not limited to:
          </p>
          <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-400">
            <li>AI-powered growth systems and lead generation</li>
            <li>Performance marketing (Meta Ads, Google Ads, LinkedIn Ads)</li>
            <li>Social media management and Google Business Profile optimization</li>
            <li>Search Engine Optimization (SEO) and content marketing</li>
            <li>Website design and conversion rate optimization</li>
            <li>WhatsApp marketing and CRM automation</li>
            <li>Healthcare and hospital-specific marketing campaigns</li>
            <li>Brand strategy and creative production</li>
          </ul>
          <p className="mt-4">
            Specific deliverables, timelines, and pricing for each engagement will be outlined in a separate written proposal, agreement, or statement of work.
          </p>
        </>
      )
    },
    {
      title: "3. Eligibility",
      content: (
        <p>
          You must be at least 18 years of age and legally capable of entering into binding contracts to use our services. By using our website or engaging our services, you represent and warrant that you meet these requirements.
        </p>
      )
    },
    {
      title: "4. Account Registration and Security",
      content: (
        <>
          <p>
            Certain features of our services (including the client/admin dashboard) require account registration. When creating an account, you agree to:
          </p>
          <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-400">
            <li>Provide accurate, current, and complete information</li>
            <li>Maintain and promptly update your account information</li>
            <li>Keep your password and credentials confidential</li>
            <li>Be solely responsible for all activities under your account</li>
            <li>Notify us immediately of any unauthorized access or security breach</li>
          </ul>
          <p className="mt-4">
            We reserve the right to suspend or terminate accounts that violate these Terms or are involved in suspicious activity.
          </p>
        </>
      )
    },
    {
      title: "5. User Responsibilities and Acceptable Use",
      content: (
        <>
          <p>You agree NOT to:</p>
          <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-400">
            <li>Use our services for any unlawful, fraudulent, or harmful purpose</li>
            <li>Submit false, misleading, or defamatory information</li>
            <li>Infringe on the intellectual property rights of others</li>
            <li>Upload viruses, malware, or any malicious code</li>
            <li>Attempt to gain unauthorized access to our systems, accounts, or networks</li>
            <li>Interfere with or disrupt the operation of our website or services</li>
            <li>Use our services to send spam, unsolicited messages, or harassment</li>
            <li>Scrape, copy, or reproduce our content without written permission</li>
            <li>Resell, sublicense, or redistribute our services without authorization</li>
          </ul>
          <p className="mt-4">
            Violation of these terms may result in immediate termination of services and legal action.
          </p>
        </>
      )
    },
    {
      title: "6. Payment Terms",
      content: (
        <>
          <p>
            Pricing, payment schedules, and billing terms will be specified in your service agreement or proposal. Unless otherwise stated:
          </p>
          <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-400">
            <li>All fees are quoted in Indian Rupees (INR) and are exclusive of applicable taxes (GST)</li>
            <li>Advance payment may be required to commence work</li>
            <li>Recurring services (monthly retainers) are billed in advance</li>
            <li>Late payments may incur a service interruption and/or interest charges</li>
            <li>All payments are non-refundable unless explicitly stated in a written agreement</li>
            <li>Ad spend budgets (Meta, Google, etc.) are billed separately from service fees</li>
          </ul>
        </>
      )
    },
    {
      title: "7. Project Deliverables and Timelines",
      content: (
        <>
          <p>
            We commit to delivering services with professional standards and within mutually agreed timelines. However:
          </p>
          <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-400">
            <li>Timelines may be affected by client delays in providing feedback, content, or approvals</li>
            <li>Marketing performance depends on many external factors (market conditions, ad platform algorithms, competition) and specific results cannot be guaranteed</li>
            <li>We will provide best-effort recommendations based on industry expertise and data</li>
            <li>Client revisions beyond the scope of work may incur additional charges</li>
          </ul>
        </>
      )
    },
    {
      title: "8. Intellectual Property Rights",
      content: (
        <>
          <p>
            All content on our website—including text, graphics, logos, images, videos, designs, software, and code—is owned by OmniCore X Quantum or our licensors and protected by intellectual property laws.
          </p>
          <p className="mt-4">
            <strong className="text-white">Client Deliverables:</strong> Upon full payment, ownership of final approved deliverables (creatives, content, designs) transfers to the client, except for:
          </p>
          <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-400">
            <li>Pre-existing tools, frameworks, templates, and methodologies developed by us</li>
            <li>Third-party assets licensed for the project (stock images, fonts, plugins)</li>
            <li>Our proprietary AI systems, processes, and trade secrets</li>
          </ul>
          <p className="mt-4">
            We retain the right to display completed work in our portfolio, case studies, and marketing materials unless explicitly restricted in writing.
          </p>
        </>
      )
    },
    {
      title: "9. Confidentiality",
      content: (
        <p>
          Both parties agree to maintain confidentiality regarding non-public business information, strategies, financial data, and trade secrets shared during the engagement. This obligation survives termination of services. Confidential information may only be disclosed when required by law or with prior written consent.
        </p>
      )
    },
    {
      title: "10. Cancellation and Termination",
      content: (
        <>
          <p>
            Either party may terminate ongoing services with prior written notice as specified in the service agreement (typically 30 days for monthly retainers).
          </p>
          <p className="mt-4">We may suspend or terminate services immediately if:</p>
          <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-400">
            <li>Payment is overdue beyond the agreed grace period</li>
            <li>Client breaches these Terms or the service agreement</li>
            <li>Client engages in unethical, illegal, or harmful behavior</li>
            <li>Continuation would expose us to legal or reputational risk</li>
          </ul>
          <p className="mt-4">
            Upon termination, all outstanding fees become immediately payable, and we will deliver any completed work for which payment has been received.
          </p>
        </>
      )
    },
    {
      title: "11. Disclaimers and No Guarantee of Results",
      content: (
        <>
          <p>
            Our services are provided on an &ldquo;as-is&rdquo; and &ldquo;as-available&rdquo; basis. While we apply industry best practices and proven strategies, we make NO guarantees regarding specific marketing results, including but not limited to:
          </p>
          <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-400">
            <li>Number of leads, sales, or conversions</li>
            <li>Specific search engine rankings or organic traffic volumes</li>
            <li>Ad platform performance (CPM, CTR, CPL)</li>
            <li>Return on Investment (ROI) or revenue figures</li>
          </ul>
          <p className="mt-4">
            Marketing outcomes depend on multiple variables outside our control, including product-market fit, pricing, sales execution, market conditions, and platform algorithm changes.
          </p>
        </>
      )
    },
    {
      title: "12. Limitation of Liability",
      content: (
        <>
          <p>
            To the maximum extent permitted by law, OmniCore X Quantum, its founders, employees, and affiliates shall NOT be liable for any:
          </p>
          <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-400">
            <li>Indirect, incidental, special, consequential, or punitive damages</li>
            <li>Loss of profits, revenue, business opportunities, or goodwill</li>
            <li>Loss of data or business interruption</li>
            <li>Damages arising from third-party platforms (Meta, Google, etc.) policy changes or account suspensions</li>
            <li>Damages exceeding the total amount paid by the client for the services in the three (3) months preceding the claim</li>
          </ul>
          <p className="mt-4">
            This limitation applies regardless of the legal theory (contract, tort, negligence, strict liability, or otherwise).
          </p>
        </>
      )
    },
    {
      title: "13. Indemnification",
      content: (
        <p>
          You agree to indemnify, defend, and hold harmless OmniCore X Quantum and its team from any claims, damages, losses, liabilities, costs, or expenses (including reasonable legal fees) arising out of: (a) your use of our services; (b) your violation of these Terms; (c) your violation of any third-party rights; (d) any content, data, or materials you provide; or (e) any breach of applicable laws or regulations.
        </p>
      )
    },
    {
      title: "14. Third-Party Services and Platforms",
      content: (
        <p>
          Our services may involve the use of third-party platforms (e.g., Meta Ads, Google Ads, Google Analytics, MongoDB Atlas, WhatsApp Business API). We are not responsible for the availability, accuracy, policies, or actions of these third parties. Your use of such platforms is governed by their respective terms of service and privacy policies.
        </p>
      )
    },
    {
      title: "15. Modifications to the Service",
      content: (
        <p>
          We reserve the right to modify, suspend, or discontinue any part of our website or services at any time, with or without notice. We will not be liable to you or any third party for any modification, suspension, or discontinuation.
        </p>
      )
    },
    {
      title: "16. Governing Law and Dispute Resolution",
      content: (
        <>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law principles.
          </p>
          <p className="mt-4">
            Any dispute arising out of or relating to these Terms or our services shall be resolved through:
          </p>
          <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-400">
            <li>First, good-faith negotiation between the parties</li>
            <li>If unresolved within 30 days, binding arbitration under the Arbitration and Conciliation Act, 1996</li>
            <li>The arbitration shall be conducted in English with venue in India</li>
            <li>Courts in India shall have exclusive jurisdiction over any matters not subject to arbitration</li>
          </ul>
        </>
      )
    },
    {
      title: "17. Force Majeure",
      content: (
        <p>
          Neither party shall be liable for any failure or delay in performance due to circumstances beyond reasonable control, including but not limited to natural disasters, war, terrorism, pandemics, government actions, internet outages, power failures, or third-party platform disruptions.
        </p>
      )
    },
    {
      title: "18. Severability",
      content: (
        <p>
          If any provision of these Terms is found to be invalid, illegal, or unenforceable by a court of competent jurisdiction, the remaining provisions shall continue in full force and effect. The invalid provision shall be modified to the minimum extent necessary to make it enforceable.
        </p>
      )
    },
    {
      title: "19. Entire Agreement",
      content: (
        <p>
          These Terms, together with our Privacy Policy and any signed service agreements or proposals, constitute the entire agreement between you and OmniCore X Quantum regarding the use of our website and services. They supersede any prior agreements, communications, or understandings, whether oral or written.
        </p>
      )
    },
    {
      title: "20. Changes to These Terms",
      content: (
        <p>
          We may revise these Terms from time to time. The most current version will always be posted on our website with the &ldquo;Last Updated&rdquo; date. Material changes will be communicated via email or a prominent notice on the website. Your continued use of our services after changes constitutes acceptance of the revised Terms.
        </p>
      )
    },
    {
      title: "21. Contact Information",
      content: (
        <>
          <p>
            If you have any questions about these Terms of Service or need to provide formal notices, please contact us:
          </p>
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-3 text-gray-400">
              <Mail className="w-5 h-5 text-purple-400" />
              <a href={`mailto:${contactInfo.email}`} className="hover:text-purple-400 transition-colors">
                {contactInfo.email}
              </a>
            </div>
            <div className="flex items-center gap-3 text-gray-400">
              <Phone className="w-5 h-5 text-purple-400" />
              <a href={`tel:+91${contactInfo.phone}`} className="hover:text-purple-400 transition-colors">
                +91 {contactInfo.phone}
              </a>
            </div>
          </div>
        </>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black pt-32 pb-20" data-testid="terms-of-service-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 border border-purple-500/30 mb-6">
            <FileText className="w-8 h-8 text-purple-400" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Terms of <span className="bg-gradient-to-r from-purple-400 to-cyan-500 bg-clip-text text-transparent">Service</span>
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Please read these terms carefully before using our website or engaging our services.
          </p>
          <p className="text-sm text-gray-500 mt-4">Last Updated: {lastUpdated}</p>
        </div>

        {/* Content */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div
              key={section.title}
              className="bg-gradient-to-br from-gray-900/60 to-black/60 backdrop-blur-sm border border-purple-500/20 rounded-2xl p-8 hover:border-purple-500/40 transition-all duration-300"
              data-testid={`terms-section-${index + 1}`}
            >
              <h2 className="text-2xl font-bold text-white mb-4">{section.title}</h2>
              <div className="text-gray-400 leading-relaxed">
                {section.content}
              </div>
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-400 mb-4">
            Need clarification on any of these terms?
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all duration-300"
            data-testid="terms-contact-cta"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
